import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useCompanies from "../../api/company";
import ConfiguratorManager from "./ConfiguratorManager";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Building2 } from "lucide-react";

const AdminConfiguratorsManager = () => {
  const { isAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { getAllCompanies } = useCompanies();

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await getAllCompanies();
        setCompanies(data || []);
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      fetchCompanies();
    }
  }, [isAdmin, getAllCompanies]);

  if (!isAdmin) {
    return (
      <div className={`p-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
        Toegang geweigerd. Alleen voor administrators.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isDark ? "border-[#1E4FCF]" : "border-[#BFFF00]"}`}
        ></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="relative">
          <h1
            className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Admin Configurators Beheer
          </h1>
          <div
            className={`absolute bottom-0 left-0 w-1/3 h-1 -z-10 transform translate-y-1 opacity-60 ${isDark ? "bg-[#1E4FCF]" : "bg-[#BFFF00]"}`}
          ></div>
        </div>
        <p className={isDark ? "text-gray-400" : "text-gray-500"}>
          Beheer configurators voor andere bedrijven. Selecteer een bedrijf om
          hun configurators te bekijken en te bewerken.
        </p>
      </div>

      <div
        className={`rounded-xl border p-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200 shadow-sm"}`}
      >
        <div className="pb-3">
          <p
            className={`text-sm font-medium flex items-center ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            <Building2 className="h-4 w-4 mr-2 text-[#1E4FCF]" />
            Selecteer Bedrijf
          </p>
        </div>
        <Select
          value={selectedCompanyId ? String(selectedCompanyId) : ""}
          onValueChange={(value) => {
            setSelectedCompanyId(value);
          }}
        >
          <SelectTrigger
            className={`w-full md:w-[300px] ${isDark ? "border-white/[0.1] bg-white/[0.03] text-white" : "border-gray-200"}`}
          >
            <SelectValue placeholder="Kies een bedrijf..." />
          </SelectTrigger>
          <SelectContent
            className={
              isDark ? "bg-[#1a1a1a] border-white/[0.1] text-white" : ""
            }
          >
            {companies.map((company) => (
              <SelectItem key={company.id} value={String(company.id)}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCompanyId ? (
        <div className="mt-6">
          <div className="mb-4 flex items-center gap-2">
            <span
              className={`text-sm font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              Geselecteerd bedrijf:
            </span>
            <span
              className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {
                companies.find(
                  (c) => String(c.id) === String(selectedCompanyId),
                )?.name
              }
            </span>
          </div>
          <div
            className={`border-t pt-6 ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
          >
            <ConfiguratorManager
              key={selectedCompanyId}
              targetCompanyId={selectedCompanyId}
            />
          </div>
        </div>
      ) : (
        <div
          className={`flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg ${isDark ? "border-white/[0.08] bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`}
        >
          <Building2
            className={`h-12 w-12 mb-3 ${isDark ? "text-gray-600" : "text-gray-300"}`}
          />
          <h3
            className={`text-lg font-medium ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Geen bedrijf geselecteerd
          </h3>
          <p
            className={`max-w-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            Selecteer een bedrijf uit het menu hierboven om de configurators te
            beheren.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminConfiguratorsManager;
